// A wiki card on a token (issue 1155): the card a tap opens in "What's this?" (or the token menu's "Wiki"), and the DM's
// picker that links a token to a page of the campaign's wiki. The card reads the page through this origin with this
// person's own sign-in (`readWikiCard`), so the site decides: a page the DM has not shared reads as "No page", exactly
// as a token with none. The body is plain text, drawn as text, never markup. "Open the page" is the second click: the
// site's page in the compendium's named window.
//
// The site's look (issue 1541, Tom's 2026-10-25 addition): the card is a pop-up of the table's frame with the title in
// gold Cinzel and the body in Crimson Text, the wiki's own article face (the reading typeface replaces it); the body keeps
// its links — a rules entry, another wiki page — which open in the compendium's window like the card's own page. The
// page's portrait (issue 1541), when the site shows it to this person, sits beside the title as the Properties panel's
// identity portrait does (the mock-up's 52px round, gold-ringed): read through this origin (`readWikiPortrait`) and
// drawn from a blob: address, never a site URL. With none the card is as before.
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import type { EventBody } from '@dragons-table/vtt-shared';
import { COMPENDIUM_WINDOW } from '../shell/panels/CompendiumPanel';
import { siteUrl } from '../session';
import { listWikiPages, readWikiCard, readWikiPortrait, type CardAnswer, type CardPart, type WikiPageRow } from '../wiki';
import { BTN_MD, POPOVER, PRIMARY, SECTION_HEAD, TITLE } from '../shell/ui';
import { wikiCard, type WikiCardAt } from './whatsThisTool';

const btn = BTN_MD;
export const NO_PAGE = 'No page: this token has no wiki entry for you.';

/**
 * The body's parts as paragraphs: split on blank lines, each kept as typed (single breaks too), a link never split.
 * Text, never markup: a part's words are a React text node, a link's address a path on the site.
 */
function paragraphs(parts: CardPart[]): CardPart[][] {
  const out: CardPart[][] = [[]];
  for (const part of parts) {
    if (part.href) { out[out.length - 1]!.push(part); continue; }
    part.text.split(/\n{2,}/).forEach((run, i) => {
      if (i > 0) out.push([]);
      if (run) out[out.length - 1]!.push({ text: run });
    });
  }
  // Trim each paragraph's ends as the plain body was trimmed, and drop the empty ones.
  return out.map((p) => p.map((x, i) => (x.href ? x : { text: i === 0 ? x.text.replace(/^\s+/, '') : i === p.length - 1 ? x.text.replace(/\s+$/, '') : x.text })))
    .filter((p) => p.some((x) => x.href || x.text.trim()));
}

function Paragraph({ parts }: { parts: CardPart[] }): ReactNode {
  const site = siteUrl();
  return (
    <p className="whitespace-pre-line">
      {parts.map((x, i) => (x.href
        ? <a key={i} href={`${site}${x.href}`} target={COMPENDIUM_WINDOW} className="text-vtt-gold underline decoration-vtt-gold-dim underline-offset-2 hover:decoration-vtt-gold">{x.text}</a>
        : <Fragment key={i}>{x.text}</Fragment>))}
    </p>
  );
}

/** The portrait beside the card's title (issue 1541): nothing until its picture has come, nothing if it does not. */
function Portrait({ campaignId, objectId, caption, title }: { campaignId: string; objectId: string; caption: string; title: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    setSrc(null);
    void readWikiPortrait(campaignId, objectId).then((blob) => {
      if (!live || !blob) return;
      made = URL.createObjectURL(blob);
      setSrc(made);
    });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [campaignId, objectId]);
  if (!src) return null;
  return <img src={src} alt={caption || title} data-testid="wiki-card-portrait" className="h-[3.25rem] w-[3.25rem] shrink-0 rounded-full border-2 border-vtt-gold bg-vtt-raised object-cover" />;
}

/** The card, wherever it was opened from. Mounted once over the board. */
export function WikiCard({ campaignId }: { campaignId: string }) {
  const [at, setAt] = useState<WikiCardAt | null>(() => wikiCard.get());
  // The answer with the token it is for: a card opened on another token shows "Reading the page…" from its first
  // frame, never the last one's answer until the new read starts (vtt-wiki-card caught the flash).
  const [held, setHeld] = useState<{ id: string; answer: CardAnswer } | null>(null);
  const answer = held && at && held.id === at.id ? held.answer : null;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => wikiCard.subscribe(setAt), []);
  useEffect(() => {
    if (!at) return undefined;
    let live = true;
    setHeld(null);
    void readWikiCard(campaignId, at.id).then((a) => { if (live) setHeld({ id: at.id, answer: a }); });
    return () => { live = false; };
  }, [campaignId, at]);
  useEffect(() => {
    if (!at) return undefined;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); wikiCard.close(); } };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
  }, [at]);
  if (!at) return null;
  const W = 320;
  const left = Math.max(4, Math.min(at.x + 12, window.innerWidth - W - 4));
  const top = Math.max(4, Math.min(at.y + 12, window.innerHeight - 240));
  const card = answer?.kind === 'ok' ? answer.card : null;
  return (
    <div ref={ref} role="dialog" aria-label={card ? `Wiki: ${card.title}` : 'Wiki'} data-testid="wiki-card"
      className={`fixed z-50 max-h-[60vh] overflow-y-auto p-3 ${POPOVER}`} style={{ left, top, width: `min(${W}px, calc(100vw - 8px))` }}>
      <div className="mb-2 flex items-start gap-2 border-b border-stone-800 pb-2">
        {card?.portrait && <Portrait campaignId={campaignId} objectId={at.id} caption={card.portrait.caption} title={card.title} />}
        <div className="min-w-0 flex-1">
          {card ? <h2 className={`${TITLE} text-base`} data-testid="wiki-card-title">{card.title}</h2> : <h2 className={`${TITLE} text-base`}>Wiki</h2>}
          {card?.type && <p className={SECTION_HEAD} data-testid="wiki-card-type">{card.type}</p>}
        </div>
        <button type="button" className={btn} aria-label="Close the wiki card" onClick={() => wikiCard.close()}>✕</button>
      </div>
      {!answer && <p className="text-sm text-stone-400">Reading the page…</p>}
      {answer?.kind === 'none' && <p className="text-sm text-stone-300" data-testid="wiki-card-none">{NO_PAGE}</p>}
      {answer?.kind === 'refused' && <p className="text-sm text-red-300" role="alert">{answer.reason}</p>}
      {answer?.kind === 'error' && <p className="text-sm text-red-300" role="alert">The page could not be read just now.</p>}
      {card && (
        <>
          <div className="space-y-2 font-article text-[0.9375rem] leading-snug" data-testid="wiki-card-body">
            {paragraphs(card.parts ?? [{ text: card.body }]).map((p, i) => <Paragraph key={i} parts={p} />)}
          </div>
          <button type="button" className={`${PRIMARY} mt-3`} onClick={() => { window.open(`${siteUrl()}${card.url}`, COMPENDIUM_WINDOW); }}>Open the page</button>
        </>
      )}
    </div>
  );
}

/** The DM's picker: the campaign's pages; choosing one links the token (`object.wiki`), "No page" unlinks it. */
export function WikiPicker({ campaignId, tokenId, current, act, onClose }: {
  campaignId: string; tokenId: string; current: string | null; act: (body: EventBody) => void; onClose: () => void;
}) {
  const [pages, setPages] = useState<WikiPageRow[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  useEffect(() => {
    let live = true;
    void listWikiPages(campaignId).then((a) => {
      if (!live) return;
      if (a.kind === 'ok') setPages(a.pages);
      else setProblem(a.kind === 'refused' ? a.reason : "The campaign's wiki could not be read just now.");
    });
    return () => { live = false; };
  }, [campaignId]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
  }, [onClose]);
  const pick = (pageId: string | null) => { act({ kind: 'object.wiki', id: tokenId, pageId }); onClose(); };
  const q = filter.trim().toLowerCase();
  const shown = (pages ?? []).filter((p) => !q || p.title.toLowerCase().includes(q) || (p.type ?? '').toLowerCase().includes(q));
  return (
    <div role="dialog" aria-label="Wiki page" data-testid="wiki-picker" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`flex max-h-[80vh] w-full max-w-sm flex-col p-3 ${POPOVER}`}>
        <h2 className={`${TITLE} mb-2 text-base`}>Wiki page</h2>
        <input type="search" aria-label="Find a page" placeholder="Find a page" className="mb-2 rounded border border-stone-600 bg-stone-800 px-2 py-1 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {problem && <p className="text-sm text-red-300" role="alert">{problem}</p>}
        {!pages && !problem && <p className="text-sm text-stone-400">Reading the campaign's wiki…</p>}
        {pages && pages.length === 0 && <p className="text-sm text-stone-400">The campaign's wiki has no pages yet. Write one on the site, then come back.</p>}
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {shown.map((p) => (
            <li key={p.id}>
              <button type="button" aria-pressed={current === p.id} className={`flex w-full items-center gap-2 rounded-[3px] px-2 py-2 text-left text-sm hover:bg-stone-800 ${current === p.id ? 'text-amber-200' : ''}`} onClick={() => pick(p.id)}>
                <span className="min-w-0 flex-1 truncate">{p.title}</span>
                {p.type && <span className="text-xs text-stone-400">{p.type}</span>}
                <span className="text-xs text-stone-500">{p.visibility === 'gm' ? 'DM only' : 'Players'}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex gap-2">
          {current && <button type="button" className={btn} onClick={() => pick(null)}>No page</button>}
          <button type="button" className={`${btn} ml-auto`} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

type PickerListener = (tokenId: string | null) => void;
const pickerListeners = new Set<PickerListener>();
/** The token menu's "Wiki page…" opens the picker for one token. */
export const wikiPicker = {
  open(tokenId: string): void { for (const l of pickerListeners) l(tokenId); },
  subscribe(l: PickerListener): () => void { pickerListeners.add(l); return () => { pickerListeners.delete(l); }; },
};

/** The picker, mounted once: open while the DM chooses a page for a token. */
export function WikiPickerGate({ campaignId, act, currentOf }: { campaignId: string; act: (body: EventBody) => void; currentOf: (tokenId: string) => string | null }) {
  const [tokenId, setTokenId] = useState<string | null>(null);
  useEffect(() => wikiPicker.subscribe(setTokenId), []);
  if (!tokenId) return null;
  return <WikiPicker campaignId={campaignId} tokenId={tokenId} current={currentOf(tokenId)} act={act} onClose={() => setTokenId(null)} />;
}
